import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { API_BASE_URL, getCurrentMonthYear } from '../App';
import Cookies from 'js-cookie';
import StateToggleList, { regionDisplayNameById } from './StateToggleList';
import LoginModal from './LoginModal';
import NewMonthModal from './NewMonthModal';
import RemoveSightingModal from './RemoveSightingModal';
import OrangeStateChoiceModal from './OrangeStateChoiceModal';
import CurrentMonthRemoveModal from './CurrentMonthRemoveModal';
import FlagOnlyDropdown from './FlagOnlyDropdown';
import GameProgressDisplay from './GameProgressDisplay';
import VirginiaTributePatternDefs from './VirginiaTributePatternDefs';
import PlateArtPatternDefs, { PlateArtInnerBorders } from './PlateArtPatternDefs';
import { usProgressPercent } from '../lib/progressStats';
import {
  getScopeByCode,
  geoMatchMaps,
  splitProjection,
  useJurisdictionsCatalog,
} from '../lib/jurisdictionsCatalog';
import {
  isVirginiaTributeActive,
  isVirginiaTributeState,
  virginiaTributePatternId,
} from '../lib/virginiaTribute';
import {
  hasPlateArt,
  isUsPlateMap,
  plateArtPatternId,
  plateArtTintName,
} from '../lib/plateArtMap';

const FALLBACK_US_GEO = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const FALLBACK_CAN_GEO =
  'https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/canada.geojson';
const FALLBACK_MX_GEO =
  'https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/mexico.geojson';

const FILL_GRAY = '#ECEFF1';
const FILL_ORANGE = '#FF5722';
const FILL_GREEN = '#2E7D32';

const Map = ({
  user,
  visitedStates,
  setVisitedStates,
  gameKey,
  setGameKey,
  mapType,
  onBannedUser,
  playMode = 'monthly',
  removalPreference = 'always_ask',
  stateTiers = {},
  setStateTiers,
  stateSightings = {},
  setStateSightings,
  windowRegionUnions = null,
  onGameDataRefresh,
  onShareEngagementNudge,
}) => {
  const [lastClickedState, setLastClickedState] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showNewMonthModal, setShowNewMonthModal] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  /** Annual/decennial orange state: choose add (current month) vs remove (then month picker). */
  const [orangeChoiceModal, setOrangeChoiceModal] = useState(null);
  /** Annual/decennial green state: confirm remove from current month only. */
  const [currentMonthRemoveModal, setCurrentMonthRemoveModal] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const { catalog, denominators } = useJurisdictionsCatalog();
  const matchMaps = geoMatchMaps(catalog);

  useEffect(() => {
    console.log('Map component received mapType:', mapType);
  }, [mapType]);

  useEffect(() => {
    console.log('Map component received gameKey:', gameKey);
    console.log('Current month-year:', getCurrentMonthYear());
    console.log('GameKey matches current month:', gameKey === getCurrentMonthYear());
  }, [gameKey]);

  const tierForState = (stateId) => {
    if (playMode === 'monthly') {
      return visitedStates.includes(stateId) ? 'current_month' : 'none';
    }
    return stateTiers[stateId] || 'none';
  };

  const virginiaTributeOn = isVirginiaTributeActive(user);
  const plateArtOn = (user ? Boolean(user.plateArtMap) : true) && isUsPlateMap(mapType);

  const plateArtEntries = [];
  if (plateArtOn) {
    const candidateIds = playMode === 'monthly' ? visitedStates : Object.keys(stateTiers || {});
    const seen = new Set();
    for (const stateId of candidateIds) {
      if (!hasPlateArt(stateId) || seen.has(stateId)) continue;
      const tint = plateArtTintName(tierForState(stateId), playMode);
      if (!tint) continue;
      seen.add(stateId);
      plateArtEntries.push({ stateId, tint });
    }
  }

  const tributeFillForState = (stateId) => {
    if (!virginiaTributeOn || !isUsPlateMap(mapType) || !isVirginiaTributeState(stateId)) return null;
    return `url(#${virginiaTributePatternId(tierForState(stateId), playMode, stateId)})`;
  };

  const plateFillForState = (stateId) => {
    if (!plateArtOn || !hasPlateArt(stateId)) return null;
    const tint = plateArtTintName(tierForState(stateId), playMode);
    if (!tint) return null;
    return `url(#${plateArtPatternId(stateId, tint)})`;
  };

  const fillForState = (stateId) => {
    const tribute = tributeFillForState(stateId);
    if (tribute) return tribute;
    const plate = plateFillForState(stateId);
    if (plate) return plate;
    const tier = tierForState(stateId);
    if (tier === 'current_month') {
      return playMode === 'monthly' ? FILL_ORANGE : FILL_GREEN;
    }
    if (tier === 'window_only') return FILL_ORANGE;
    return FILL_GRAY;
  };

  const submitRemoveSighting = async (stateId, monthYear) => {
    setLastClickedState(stateId);
    try {
      const response = await fetch(`${API_BASE_URL}/game`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('session'),
        },
        body: JSON.stringify({ removeSighting: { stateId, monthYear } }),
      });
      const errorData = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 403 && errorData.error === 'Account banned') {
          if (onBannedUser) onBannedUser(errorData);
          return;
        }
        console.error('removeSighting failed:', errorData);
        return;
      }
      setRemoveModal(null);
      if (onGameDataRefresh) await onGameDataRefresh();
    } catch (error) {
      console.error('removeSighting failed:', error);
    }
    setTimeout(() => setLastClickedState(null), 1000);
  };

  const applyCurrentMonthMutation = async (newVisitedStates) => {
    const prevVisited = visitedStates;
    const prevTiers = stateTiers;
    const prevSightings = stateSightings;
    const currentMonthYear = getCurrentMonthYear();

    const added = newVisitedStates.filter((id) => !prevVisited.includes(id));
    const removed = prevVisited.filter((id) => !newVisitedStates.includes(id));

    setVisitedStates(newVisitedStates);

    // Annual/decennial map fill uses stateTiers — update optimistically so clicks feel instant.
    if (playMode !== 'monthly' && typeof setStateTiers === 'function') {
      const nextTiers = { ...stateTiers };
      const nextSightings = { ...stateSightings };
      for (const sid of added) {
        nextTiers[sid] = 'current_month';
        const months = nextSightings[sid] || [];
        if (!months.includes(currentMonthYear)) {
          nextSightings[sid] = [currentMonthYear, ...months];
        }
      }
      for (const sid of removed) {
        const months = (nextSightings[sid] || []).filter((m) => m !== currentMonthYear);
        if (months.length === 0) {
          delete nextTiers[sid];
          delete nextSightings[sid];
        } else {
          nextTiers[sid] = 'window_only';
          nextSightings[sid] = months;
        }
      }
      setStateTiers(nextTiers);
      if (typeof setStateSightings === 'function') {
        setStateSightings(nextSightings);
      }
    }

    const rollback = () => {
      setVisitedStates(prevVisited);
      if (playMode !== 'monthly' && typeof setStateTiers === 'function') {
        setStateTiers(prevTiers);
        if (typeof setStateSightings === 'function') {
          setStateSightings(prevSightings);
        }
      }
    };

    try {
      const response = await fetch(`${API_BASE_URL}/game`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('session'),
        },
        body: JSON.stringify({
          visitedStates: newVisitedStates,
          progress: usProgressPercent(newVisitedStates, denominators),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.error === 'Account banned') {
          rollback();
          if (onBannedUser) onBannedUser(errorData);
          return;
        }
        console.error('Failed to save game state:', errorData);
        rollback();
        return;
      }
      const saved = await response.json().catch(() => ({}));
      if (saved?.shareEngagementNudge?.show && onShareEngagementNudge) {
        onShareEngagementNudge(saved.shareEngagementNudge);
      }
      if (onGameDataRefresh) await onGameDataRefresh();
    } catch (error) {
      console.error('Failed to save game state:', error);
      rollback();
    }
  };

  const handleStateClick = async (stateId) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    const currentMonthYear = getCurrentMonthYear();

    if (playMode === 'monthly' && gameKey && gameKey !== currentMonthYear) {
      console.warn(`🚨 GAME KEY MISMATCH DETECTED!`, {
        storedGameKey: gameKey,
        currentMonthYear,
        action: 'state_click',
        stateId,
        timestamp: new Date().toISOString(),
      });
      setPendingStateChange(stateId);
      setShowNewMonthModal(true);
      return;
    }

    const tier = tierForState(stateId);

    if (tier === 'window_only') {
      if (playMode === 'monthly') {
        return;
      }
      const histMonths = (stateSightings[stateId] || []).filter((m) => m !== currentMonthYear);
      if (histMonths.length === 0) {
        return;
      }
      setOrangeChoiceModal({ stateId, histMonths });
      return;
    }

    if (tier === 'current_month') {
      if (playMode === 'monthly') {
        setLastClickedState(stateId);
        const newVisitedStates = visitedStates.filter((id) => id !== stateId);
        await applyCurrentMonthMutation(newVisitedStates);
        setTimeout(() => setLastClickedState(null), 1000);
      } else {
        setCurrentMonthRemoveModal({ stateId });
      }
      return;
    }

    setLastClickedState(stateId);
    const newVisitedStates = [...visitedStates, stateId];
    await applyCurrentMonthMutation(newVisitedStates);
    setTimeout(() => setLastClickedState(null), 1000);
  };

  const handleOrangeAddSighting = async () => {
    if (!orangeChoiceModal) return;
    const { stateId } = orangeChoiceModal;
    setOrangeChoiceModal(null);
    if (visitedStates.includes(stateId)) return;
    setLastClickedState(stateId);
    await applyCurrentMonthMutation([...visitedStates, stateId]);
    setTimeout(() => setLastClickedState(null), 1000);
  };

  const handleOrangeChooseRemove = () => {
    if (!orangeChoiceModal) return;
    const { stateId, histMonths } = orangeChoiceModal;
    setOrangeChoiceModal(null);
    if (removalPreference === 'current_month_only') {
      setInfoMessage(
        'You can only remove plates from the current month. Change this on the Account page if you need to remove older sightings.'
      );
      setTimeout(() => setInfoMessage(null), 7000);
      return;
    }
    if (histMonths.length === 0) return;
    setRemoveModal({ stateId, months: histMonths });
  };

  const confirmCurrentMonthRemove = async () => {
    if (!currentMonthRemoveModal) return;
    const stateId = currentMonthRemoveModal.stateId;
    setCurrentMonthRemoveModal(null);
    setLastClickedState(stateId);
    const newVisitedStates = visitedStates.filter((id) => id !== stateId);
    await applyCurrentMonthMutation(newVisitedStates);
    setTimeout(() => setLastClickedState(null), 1000);
  };

  const handleStateClickWithLogin = async (stateId) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    await handleStateClick(stateId);
  };

  const handleNewMonthConfirm = () => {
    const pending = pendingStateChange;
    setVisitedStates([]);
    const currentMonthYear = getCurrentMonthYear();
    setGameKey(currentMonthYear);
    setShowNewMonthModal(false);
    setPendingStateChange(null);

    if (pending) {
      const newVisitedStates = [pending];
      setVisitedStates(newVisitedStates);
      sendStateToServer(newVisitedStates);
    }
  };

  const handleNewMonthCancel = () => {
    setShowNewMonthModal(false);
    setPendingStateChange(null);
  };

  const sendStateToServer = async (newVisitedStates) => {
    try {
      const response = await fetch(`${API_BASE_URL}/game`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('session'),
        },
        body: JSON.stringify({
          visitedStates: newVisitedStates,
          progress: usProgressPercent(newVisitedStates, denominators),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.error === 'Account banned') {
          if (onBannedUser) onBannedUser(errorData);
          return;
        }
        console.error('Failed to save game state:', errorData);
        return;
      }
      if (onGameDataRefresh) await onGameDataRefresh();
    } catch (error) {
      console.error('Failed to save game state:', error);
    }
  };

  const handleDebugGameKeyChange = () => {
    const currentDate = new Date();
    const eastTime = new Date(currentDate.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const priorMonth = new Date(eastTime.getFullYear(), eastTime.getMonth() - 1, 1);
    const priorMonthYear =
      priorMonth.toLocaleString('default', { month: 'long' }) + '-' + priorMonth.getFullYear();
    setGameKey(priorMonthYear);
    setInfoMessage(
      `Local only: game key set to ${priorMonthYear}. Refresh or use “Seed sample history” to persist buckets on the server.`
    );
    setTimeout(() => setInfoMessage(null), 8000);
  };

  const handleSeedSampleHistory = async () => {
    const session = Cookies.get('session');
    if (!session) return;
    setSeedBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/dev/seed-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: session,
        },
        body: '{}',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setInfoMessage(data.error || `Seed failed (${res.status})`);
        setTimeout(() => setInfoMessage(null), 7000);
        return;
      }
      setInfoMessage(
        'Sample history written (current month, January 2026, August 2021). Open Account and switch Annual or Decennial to compare map colors.'
      );
      setTimeout(() => setInfoMessage(null), 9000);
      if (onGameDataRefresh) await onGameDataRefresh();
    } catch (e) {
      console.error(e);
      setInfoMessage('Could not reach the worker. Is wrangler dev on :8787?');
      setTimeout(() => setInfoMessage(null), 7000);
    } finally {
      setSeedBusy(false);
    }
  };

  const mapBody = (realUrl, projection, className, pconfig) => (
    <div className="map-container">
      <div className="map-top-bar">
        <GameProgressDisplay
          variant="desktop"
          mapType={mapType}
          playMode={playMode}
          visitedStates={visitedStates}
          stateTiers={stateTiers}
          stateSightings={stateSightings}
          currentMonthYear={getCurrentMonthYear()}
          windowRegionUnions={windowRegionUnions}
        />
        <div className="game-description">
          Welcome to Plate Chase! Click on states as you spot their license plates on the road. Your progress is
          automatically saved, and you can sync across devices by signing in.
        </div>
        <div className="map-top-bar__flag">
          <FlagOnlyDropdown currentMapType={mapType} />
        </div>
      </div>
      {infoMessage && (
        <div className="map-info-banner" role="status">
          {infoMessage}
        </div>
      )}
      {process.env.NODE_ENV === 'development' && user && (
        <div className="map-dev-panel">
          <span className="map-dev-panel__label">Local dev</span>
          <button
            type="button"
            className="map-dev-panel__btn map-dev-panel__btn--primary"
            onClick={handleSeedSampleHistory}
            disabled={seedBusy}
          >
            {seedBusy ? 'Seeding…' : 'Seed sample history (Jan 2026, Aug 2021, current month)'}
          </button>
          <button type="button" className="map-dev-panel__btn" onClick={handleDebugGameKeyChange}>
            Set prior month key (client only)
          </button>
          <button
            type="button"
            className="map-dev-panel__btn map-dev-panel__btn--accent"
            onClick={() =>
              onShareEngagementNudge?.({
                show: true,
                milestone: 10,
                lifetimePlatesLogged: 10,
              })
            }
          >
            Preview share nudge modal
          </button>
        </div>
      )}
      <ComposableMap projection={projection} className={className} projectionConfig={pconfig}>
        {plateArtEntries.length ? <PlateArtPatternDefs entries={plateArtEntries} /> : null}
        {virginiaTributeOn && isUsPlateMap(mapType) ? <VirginiaTributePatternDefs /> : null}
        <Geographies geography={realUrl}>
          {({ geographies }) => {
            const borderEntries = [];
            const shapes = geographies.map((geo) => {
              let tempId = geo.id;
              const featureName = geo.properties && geo.properties.name;
              if (!tempId && featureName) {
                tempId =
                  matchMaps.CAN[featureName] ||
                  matchMaps.MX[featureName] ||
                  matchMaps.US[featureName];
              }

              const plateTint = plateArtOn ? plateArtTintName(tierForState(tempId), playMode) : null;
              if (plateTint && geo.svgPath) {
                borderEntries.push({ stateId: tempId, tint: plateTint, svgPath: geo.svgPath });
              }

              const plateUnder = tributeFillForState(tempId) ? plateFillForState(tempId) : null;
              const fill = fillForState(tempId);
              // The plate picture sits underneath. The shape on top is what turns blue
              // and shows the hand, the same as every other state.
              const plateHit = Boolean(plateTint) && !plateUnder;
              const isAnimating = lastClickedState === tempId;
              const seam = plateTint
                ? { stroke: '#FFF', strokeWidth: 0.45 }
                : { stroke: '#FFF', strokeWidth: 0.5 };
              const interactive = (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onClick={() => handleStateClick(tempId)}
                  tabIndex={-1}
                  style={{
                    default: {
                      fill: plateHit ? 'rgba(0,0,0,0.004)' : fill,
                      ...(plateHit ? { stroke: 'none', pointerEvents: 'all' } : seam),
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      animation: isAnimating ? 'pulse 1s ease-in-out' : 'none',
                      outline: 'none',
                    },
                    hover: {
                      fill: '#2196F3',
                      stroke: '#FFF',
                      strokeWidth: 0.5,
                      ...(plateHit ? { pointerEvents: 'all' } : {}),
                      cursor: 'pointer',
                      outline: 'none',
                    },
                    pressed: {
                      fill: plateHit ? 'rgba(0,0,0,0.004)' : fill,
                      ...(plateHit ? { stroke: 'none', pointerEvents: 'all' } : seam),
                      cursor: 'pointer',
                      outline: 'none',
                    },
                    active: {
                      fill: plateHit ? 'rgba(0,0,0,0.004)' : fill,
                      ...(plateHit ? { stroke: 'none', pointerEvents: 'all' } : seam),
                      cursor: 'pointer',
                      outline: 'none',
                    },
                  }}
                />
              );
              if (plateUnder) {
                return [
                  <Geography
                    key={`${geo.rsmKey}-plate`}
                    geography={geo}
                    style={{
                      default: { fill: plateUnder, stroke: 'none', pointerEvents: 'none', outline: 'none' },
                      hover: { fill: plateUnder, stroke: 'none', pointerEvents: 'none', outline: 'none' },
                      pressed: { fill: plateUnder, stroke: 'none', pointerEvents: 'none', outline: 'none' },
                    }}
                  />,
                  interactive,
                ];
              }
              if (!plateHit) return interactive;
              return [
                <Geography
                  key={`${geo.rsmKey}-plate-visual`}
                  geography={geo}
                  style={{
                    default: { fill, ...seam, pointerEvents: 'none', outline: 'none' },
                    hover: { fill, ...seam, pointerEvents: 'none', outline: 'none' },
                    pressed: { fill, ...seam, pointerEvents: 'none', outline: 'none' },
                  }}
                />,
                interactive,
              ];
            });
            return (
              <>
                {shapes}
                <PlateArtInnerBorders entries={borderEntries} />
              </>
            );
          }}
        </Geographies>
      </ComposableMap>
      <StateToggleList
        visitedStates={visitedStates}
        onStateClick={handleStateClickWithLogin}
        gameKey={gameKey}
        mapType={mapType}
        playMode={playMode}
        stateTiers={stateTiers}
        virginiaTributeOn={virginiaTributeOn}
      />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <NewMonthModal
        isOpen={showNewMonthModal}
        onClose={handleNewMonthCancel}
        onConfirm={handleNewMonthConfirm}
        previousMonth={gameKey}
        currentMonth={getCurrentMonthYear()}
      />
      <OrangeStateChoiceModal
        isOpen={!!orangeChoiceModal}
        stateLabel={regionDisplayNameById(orangeChoiceModal?.stateId)}
        currentMonthYear={getCurrentMonthYear()}
        removalPreference={removalPreference}
        onClose={() => setOrangeChoiceModal(null)}
        onAddSighting={handleOrangeAddSighting}
        onRemoveSighting={handleOrangeChooseRemove}
      />
      <CurrentMonthRemoveModal
        isOpen={!!currentMonthRemoveModal}
        stateLabel={regionDisplayNameById(currentMonthRemoveModal?.stateId)}
        currentMonthYear={getCurrentMonthYear()}
        onClose={() => setCurrentMonthRemoveModal(null)}
        onConfirm={confirmCurrentMonthRemove}
      />
      <RemoveSightingModal
        isOpen={!!removeModal}
        stateLabel={regionDisplayNameById(removeModal?.stateId)}
        months={removeModal?.months || []}
        onClose={() => setRemoveModal(null)}
        onSelectMonth={(monthYear) => {
          if (removeModal) submitRemoveSighting(removeModal.stateId, monthYear);
        }}
      />
    </div>
  );

  if (mapType === 'US' || mapType === 'COMBINED' || mapType === 'CAN' || mapType === 'MX') {
    const scopeMeta = getScopeByCode(mapType, catalog);
    const { projection, projectionConfig } = splitProjection(scopeMeta?.projection);
    let realUrl = scopeMeta?.geoUrl;
    let className = 'us-map';

    if (mapType === 'CAN') {
      realUrl = realUrl || FALLBACK_CAN_GEO;
      className = 'canada-map';
    } else if (mapType === 'MX') {
      realUrl = realUrl || FALLBACK_MX_GEO;
      className = 'mexico-map';
    } else {
      realUrl = realUrl || FALLBACK_US_GEO;
      className = 'us-map';
    }

    let pconfig = projectionConfig;
    if (!pconfig && mapType === 'CAN') {
      pconfig = {
        rotate: [96, 0, 0],
        scale: 800,
        center: [-0, 64],
        parallels: [49, 80],
        clipExtent: [
          [0, 0],
          [100, 500],
        ],
      };
    } else if (!pconfig && mapType === 'MX') {
      pconfig = {
        rotate: [102, 0, 0],
        scale: 1500,
        center: [0, 24],
        parallels: [17, 32],
      };
    }

    return mapBody(realUrl, projection, className, pconfig);
  }

  return (
    <div className="map-container">
      <div className="map-top-bar">
        <GameProgressDisplay
          variant="desktop"
          mapType={mapType}
          playMode={playMode}
          visitedStates={visitedStates}
          stateTiers={stateTiers}
          stateSightings={stateSightings}
          currentMonthYear={getCurrentMonthYear()}
          windowRegionUnions={windowRegionUnions}
        />
        <div className="game-description">
          Welcome to Plate Chase! Click on states as you spot their license plates on the road. Your progress is
          automatically saved, and you can sync across devices by signing in.
        </div>
        <div className="map-top-bar__flag">
          <FlagOnlyDropdown currentMapType={mapType} />
        </div>
      </div>
      <div className="map-type-display">
        <h2>Map Type: {mapType}</h2>
        <p>This map type is not yet implemented. Current map parameter: {mapType}</p>
      </div>
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <NewMonthModal
        isOpen={showNewMonthModal}
        onClose={handleNewMonthCancel}
        onConfirm={handleNewMonthConfirm}
        previousMonth={gameKey}
        currentMonth={getCurrentMonthYear()}
      />
      <OrangeStateChoiceModal
        isOpen={!!orangeChoiceModal}
        stateLabel={regionDisplayNameById(orangeChoiceModal?.stateId)}
        currentMonthYear={getCurrentMonthYear()}
        removalPreference={removalPreference}
        onClose={() => setOrangeChoiceModal(null)}
        onAddSighting={handleOrangeAddSighting}
        onRemoveSighting={handleOrangeChooseRemove}
      />
      <CurrentMonthRemoveModal
        isOpen={!!currentMonthRemoveModal}
        stateLabel={regionDisplayNameById(currentMonthRemoveModal?.stateId)}
        currentMonthYear={getCurrentMonthYear()}
        onClose={() => setCurrentMonthRemoveModal(null)}
        onConfirm={confirmCurrentMonthRemove}
      />
      <RemoveSightingModal
        isOpen={!!removeModal}
        stateLabel={regionDisplayNameById(removeModal?.stateId)}
        months={removeModal?.months || []}
        onClose={() => setRemoveModal(null)}
        onSelectMonth={(monthYear) => {
          if (removeModal) submitRemoveSighting(removeModal.stateId, monthYear);
        }}
      />
    </div>
  );
};

export default Map;
